import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n, type Language } from "../context/i18n-context";
import { useBrowserStorageConsent } from "../context/browser-storage-consent-context";
import { BROWSER_STORAGE_KEYS } from "../browser-storage";
import { useAiConsent } from "../context/ai-consent-context";
import { useOnboarding } from "../context/onboarding-context";
import {
  chooseGuidePosition,
  type GuidePosition,
} from "./onboarding-guide-position";

const GUIDE_POSITION_STORAGE_KEY = BROWSER_STORAGE_KEYS.onboardingGuidePosition;
const GUIDE_COLLAPSED_STORAGE_KEY = BROWSER_STORAGE_KEYS.onboardingGuideCollapsed;

function readStoredGuidePosition(allowOptionalStorage: boolean): GuidePosition | null {
  if (!allowOptionalStorage) return null;
  try {
    const stored = window.localStorage.getItem(GUIDE_POSITION_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Partial<GuidePosition>;
    if (!Number.isFinite(parsed.left) || !Number.isFinite(parsed.top))
      return null;
    return { left: Number(parsed.left), top: Number(parsed.top) };
  } catch {
    return null;
  }
}

function storeGuidePosition(position: GuidePosition, allowOptionalStorage: boolean) {
  if (!allowOptionalStorage) return;
  try {
    window.localStorage.setItem(
      GUIDE_POSITION_STORAGE_KEY,
      JSON.stringify(position)
    );
  } catch {
    // Position memory is a convenience and should not block onboarding.
  }
}

function readStoredGuideCollapsed(allowOptionalStorage: boolean) {
  if (!allowOptionalStorage) return false;
  try {
    return window.localStorage.getItem(GUIDE_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function storeGuideCollapsed(collapsed: boolean, allowOptionalStorage: boolean) {
  if (!allowOptionalStorage) return;
  try {
    window.localStorage.setItem(
      GUIDE_COLLAPSED_STORAGE_KEY,
      String(collapsed),
    );
  } catch {
    // Collapse memory is a convenience and should not block onboarding.
  }
}

function isCompactViewport() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 700px)").matches
  );
}

const stepCopy: Record<
  string,
  { titleKey: string; descriptionKey: string; route: string }
> = {
  AI_CONSENT: {
    titleKey: "onboarding.step.aiConsent.title",
    descriptionKey: "onboarding.step.aiConsent.description",
    route: "/",
  },
  GLOBAL_GOAL: {
    titleKey: "onboarding.step.globalGoal.title",
    descriptionKey: "onboarding.step.globalGoal.description",
    route: "/planning?mode=year",
  },
  YEAR_GOAL: {
    titleKey: "onboarding.step.yearGoal.title",
    descriptionKey: "onboarding.step.yearGoal.description",
    route: "/planning?mode=year",
  },
  MONTHLY_PRIORITY: {
    titleKey: "onboarding.step.monthlyPriority.title",
    descriptionKey: "onboarding.step.monthlyPriority.description",
    route: "/planning?mode=month",
  },
  WEEKLY_GOAL: {
    titleKey: "onboarding.step.weeklyGoal.title",
    descriptionKey: "onboarding.step.weeklyGoal.description",
    route: "/planning?mode=week",
  },
  TODAY_PLAN: {
    titleKey: "onboarding.step.todayPlan.title",
    descriptionKey: "onboarding.step.todayPlan.description",
    route: "/planning",
  },
  DAILY_TASK: {
    titleKey: "onboarding.step.dailyTask.title",
    descriptionKey: "onboarding.step.dailyTask.description",
    route: "/",
  },
  STREAK: {
    titleKey: "onboarding.step.streak.title",
    descriptionKey: "onboarding.step.streak.description",
    route: "/",
  },
  REFLECTION: {
    titleKey: "onboarding.step.reflection.title",
    descriptionKey: "onboarding.step.reflection.description",
    route: "/",
  },
  AI_RECOMMENDATION: {
    titleKey: "onboarding.step.aiRecommendation.title",
    descriptionKey: "onboarding.step.aiRecommendation.description",
    route: "/",
  },
  RETURN: {
    titleKey: "onboarding.step.return.title",
    descriptionKey: "onboarding.step.return.description",
    route: "/",
  },
};

function statusKey(status: string) {
  if (status === "SKIPPED") return "onboarding.statusSkipped";
  if (status === "COMPLETED") return "onboarding.statusDone";
  return "onboarding.statusNext";
}

export function OnboardingGuide() {
  const { journey, paused, pause, resumeGuide, skipJourney } = useOnboarding();
  const { open: aiConsentOpen, requestAiAccess } = useAiConsent();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, t } = useI18n();
  const { isOptionalStorageEnabled } = useBrowserStorageConsent();
  const [hasTarget, setHasTarget] = useState(false);
  const [guidePosition, setGuidePosition] = useState<GuidePosition | null>(
    () => readStoredGuidePosition(isOptionalStorageEnabled)
  );
  const [hasManualPosition, setHasManualPosition] = useState(
    () => guidePosition !== null
  );
  const [isCollapsed, setIsCollapsed] = useState(() => readStoredGuideCollapsed(isOptionalStorageEnabled));
  const [isCompact, setIsCompact] = useState(isCompactViewport);
  const [isDragging, setIsDragging] = useState(false);
  const [isConfirmingSkip, setIsConfirmingSkip] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [skipError, setSkipError] = useState(false);
  const [layoutVersion, setLayoutVersion] = useState(0);
  const guideRef = useRef<HTMLElement | null>(null);
  const guidePositionRef = useRef<GuidePosition | null>(guidePosition);
  const manualPositionRef = useRef<GuidePosition | null>(guidePosition);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
  } | null>(null);
  const aiConsentPromptedRef = useRef(false);
  const currentKey = journey?.currentStep ?? "";
  const copy = stepCopy[currentKey];

  useEffect(() => {
    if (isOptionalStorageEnabled) return;
    guidePositionRef.current = null;
    manualPositionRef.current = null;
    setGuidePosition(null);
    setHasManualPosition(false);
    setIsCollapsed(false);
  }, [isOptionalStorageEnabled]);

  useEffect(() => {
    if (currentKey !== "AI_CONSENT") {
      aiConsentPromptedRef.current = false;
      return;
    }
    if (aiConsentPromptedRef.current || aiConsentOpen) return;
    aiConsentPromptedRef.current = true;
    void requestAiAccess();
  }, [aiConsentOpen, currentKey, requestAiAccess]);

  useEffect(() => {
    if (!journey?.active || paused || !currentKey) return;
    let attempts = 0;
    let timer = 0;
    const focusTarget = () => {
      attempts += 1;
      const target = document.querySelector(
        `[data-onboarding-target="${currentKey}"]`
      );
      setHasTarget(Boolean(target));
      document
        .querySelectorAll(".onboarding-target-active")
        .forEach((node) => node.classList.remove("onboarding-target-active"));
      if (target) {
        if (currentKey !== "AI_CONSENT") {
          target.classList.add("onboarding-target-active");
          target.setAttribute("data-onboarding-hint", t("onboarding.clickHere"));
        }
        (target as HTMLElement).scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
      if (attempts < 20) timer = window.setTimeout(focusTarget, 100);
    };
    focusTarget();
    return () => window.clearTimeout(timer);
  }, [currentKey, journey?.active, pathname, paused, t]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 700px)");
    const handleMediaChange = () => setIsCompact(media.matches);
    handleMediaChange();
    media.addEventListener("change", handleMediaChange);
    return () => media.removeEventListener("change", handleMediaChange);
  }, []);

  useEffect(() => {
    const handleResize = () => setLayoutVersion((version) => version + 1);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!journey?.active || !currentKey || isCompact) return;
    let frame = 0;
    const positionGuide = () => {
      const guide = guideRef.current;
      if (!guide) return;
      const targetElement = document.querySelector<HTMLElement>(
        `[data-onboarding-target="${currentKey}"]`
      );
      const target = targetElement?.getBoundingClientRect();
      const guideRect = guide.getBoundingClientRect();
      const next = chooseGuidePosition({
        target: target
          ? {
              left: target.left,
              top: target.top,
              width: target.width,
              height: target.height,
            }
          : null,
        guideSize: { width: guideRect.width, height: guideRect.height },
        viewport: { width: window.innerWidth, height: window.innerHeight },
        manualPosition: manualPositionRef.current,
      });
      guidePositionRef.current = next;
      setGuidePosition(next);
    };
    frame = window.requestAnimationFrame(positionGuide);
    return () => window.cancelAnimationFrame(frame);
  }, [currentKey, hasTarget, isCollapsed, isCompact, journey?.active, layoutVersion, pathname, paused]);

  useEffect(
    () => () => {
      document
        .querySelectorAll(".onboarding-target-active")
        .forEach((node) => node.classList.remove("onboarding-target-active"));
    },
    []
  );

  const steps = useMemo(() => journey?.steps ?? [], [journey?.steps]);
  if (!journey?.active || !copy) return null;
  const activeJourney = journey;
  const completedCount = steps.filter(
    (step) => step.status === "COMPLETED" || step.status === "SKIPPED",
  ).length;

  function toggleCollapsed() {
    setIsCollapsed((collapsed) => {
      const next = !collapsed;
      storeGuideCollapsed(next, isOptionalStorageEnabled);
      return next;
    });
  }

  function updateGuidePosition(next: GuidePosition) {
    guidePositionRef.current = next;
    setGuidePosition(next);
  }

  function guideSize() {
    const rect = guideRef.current?.getBoundingClientRect();
    return {
      width: rect?.width ?? 360,
      height: rect?.height ?? 420,
    };
  }

  function nudgeGuide(event: KeyboardEvent<HTMLButtonElement>) {
    const delta = 24;
    const offsets: Record<string, GuidePosition> = {
      ArrowLeft: { left: -delta, top: 0 },
      ArrowRight: { left: delta, top: 0 },
      ArrowUp: { left: 0, top: -delta },
      ArrowDown: { left: 0, top: delta },
    };
    const offset = offsets[event.key];
    if (!offset) return;
    event.preventDefault();
    const guideRect = guideRef.current?.getBoundingClientRect();
    const current = guideRect
      ? { left: guideRect.left, top: guideRect.top }
      : guidePositionRef.current ?? { left: 16, top: 16 };
    const next = chooseGuidePosition({
      target: null,
      guideSize: guideSize(),
      viewport: { width: window.innerWidth, height: window.innerHeight },
      manualPosition: {
        left: current.left + offset.left,
        top: current.top + offset.top,
      },
    });
    manualPositionRef.current = next;
    setHasManualPosition(true);
    updateGuidePosition(next);
    storeGuidePosition(next, isOptionalStorageEnabled);
  }

  function startGuideDrag(event: PointerEvent<HTMLButtonElement>) {
    if (event.button !== 0) return;
    const guideRect = guideRef.current?.getBoundingClientRect();
    const current = guideRect
      ? { left: guideRect.left, top: guideRect.top }
      : guidePositionRef.current ?? { left: 16, top: 16 };
    manualPositionRef.current = current;
    setHasManualPosition(true);
    updateGuidePosition(current);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: current.left,
      startTop: current.top,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
    event.preventDefault();
  }

  function moveGuide(event: PointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const next = chooseGuidePosition({
      target: null,
      guideSize: guideSize(),
      viewport: { width: window.innerWidth, height: window.innerHeight },
      manualPosition: {
        left: drag.startLeft + event.clientX - drag.startX,
        top: drag.startTop + event.clientY - drag.startY,
      },
    });
    updateGuidePosition(next);
  }

  function finishGuideDrag(event: PointerEvent<HTMLButtonElement>) {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId)
      return;
    const next = guidePositionRef.current;
    dragRef.current = null;
    setIsDragging(false);
    if (next) {
      manualPositionRef.current = next;
      storeGuidePosition(next, isOptionalStorageEnabled);
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function openStep(stepKey = currentKey) {
    if (stepKey === "RETURN") {
      window.location.reload();
      return;
    }
    const step = stepCopy[stepKey];
    if (!step) return;
    if (stepKey === "AI_CONSENT") {
      aiConsentPromptedRef.current = true;
      void requestAiAccess();
      return;
    }
    const executionRoute =
      activeJourney.executionDate &&
      ["DAILY_TASK", "STREAK", "REFLECTION", "AI_RECOMMENDATION"].includes(stepKey)
        ? `/?date=${activeJourney.executionDate}`
        : step.route;
    navigate(executionRoute);
    resumeGuide();
    if (isCompact) {
      setIsCollapsed(true);
      storeGuideCollapsed(true, isOptionalStorageEnabled);
    }
  }

  async function confirmSkip() {
    setIsSkipping(true);
    setSkipError(false);
    try {
      const skipped = await skipJourney();
      if (!skipped) setSkipError(true);
    } catch {
      setSkipError(true);
    } finally {
      setIsSkipping(false);
    }
  }

  return (
    <>
      <aside
        ref={guideRef}
        className={`onboarding-guide${paused ? " onboarding-guide--paused" : ""}${isDragging ? " onboarding-guide--dragging" : ""}${isCollapsed ? " onboarding-guide--collapsed" : ""}${hasManualPosition ? " onboarding-guide--positioned" : ""}`}
        aria-label={t("onboarding.aria")}
        style={
          guidePosition && !isCompact && (!isCollapsed || hasManualPosition)
            ? {
                left: `${guidePosition.left}px`,
                top: `${guidePosition.top}px`,
                bottom: "auto",
              }
            : undefined
        }
      >
        <div className="onboarding-guide__head">
          <div>
            <p className="label">{t("onboarding.label")}</p>
            <strong>
              {isCollapsed ? t(copy.titleKey) : t("onboarding.title")}
            </strong>
          </div>
          <div className="onboarding-guide__head-actions">
            <label className="onboarding-guide__language">
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value as Language)}
                aria-label={t("app.language")}
                title={t("app.language")}
              >
                <option value="en">{t("lang.enShort")}</option>
                <option value="pl">{t("lang.plShort")}</option>
                <option value="uk">{t("lang.ukShort")}</option>
              </select>
            </label>
            <button
              type="button"
              className="onboarding-guide__drag-handle"
              aria-label={t("onboarding.moveGuide")}
              title={t("onboarding.moveGuide")}
              onKeyDown={nudgeGuide}
              onPointerDown={startGuideDrag}
              onPointerMove={moveGuide}
              onPointerUp={finishGuideDrag}
              onPointerCancel={finishGuideDrag}
              onLostPointerCapture={finishGuideDrag}
            >
              <span aria-hidden="true">⠿</span>
            </button>
            <span className="onboarding-guide__count">
              {t("onboarding.progress", {
                completed: completedCount,
                total: steps.length,
              })}
            </span>
            <button
              type="button"
              className="onboarding-guide__collapse-toggle"
              aria-expanded={!isCollapsed}
              aria-controls="onboarding-guide-content"
              aria-label={t(isCollapsed ? "onboarding.expand" : "onboarding.collapse")}
              title={t(isCollapsed ? "onboarding.expand" : "onboarding.collapse")}
              onClick={toggleCollapsed}
            >
              <span
                className="onboarding-guide__collapse-icon"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div className="onboarding-guide__progress" aria-hidden="true">
          <span
            style={{
              width: `${Math.round((completedCount / Math.max(steps.length, 1)) * 100)}%`,
            }}
          />
        </div>
        <div id="onboarding-guide-content" className="onboarding-guide__content" hidden={isCollapsed}>
          <ol className="onboarding-guide__steps">
            {steps.map((step) => (
              <li
                key={step.key}
                className={`onboarding-guide__step onboarding-guide__step--${step.status.toLowerCase()}`}
              >
                <button
                  type="button"
                  className="onboarding-guide__step-button"
                  onClick={() =>
                    step.status === "ACTIVE" ? openStep(step.key) : undefined
                  }
                  disabled={step.status !== "ACTIVE"}
                  aria-current={step.status === "ACTIVE" ? "step" : undefined}
                >
                  <span
                    className="onboarding-guide__step-mark"
                    aria-hidden="true"
                  >
                    {step.status === "COMPLETED" || step.status === "SKIPPED"
                      ? "✓"
                      : step.order + 1}
                  </span>
                  <span>
                    {step.key === "ACCOUNT"
                      ? t("onboarding.account")
                      : step.key === "PROFILE"
                        ? t("onboarding.profile")
                    : stepCopy[step.key]
                      ? t(stepCopy[step.key].titleKey)
                      : step.key}
                  </span>
                  <small>{t(statusKey(step.status))}</small>
                </button>
              </li>
            ))}
          </ol>
          {!paused ? (
            <div className="onboarding-guide__active">
              <strong>{t(copy.titleKey)}</strong>
              <p>{t(copy.descriptionKey)}</p>
              {!hasTarget ? (
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => openStep()}
                >
                  {currentKey === "RETURN"
                    ? t("onboarding.verify")
                    : t("onboarding.open")}
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={pause}
              >
                {t("onboarding.pause")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={resumeGuide}
            >
              {t("onboarding.resume")}
            </button>
          )}
          <div className="onboarding-guide__skip">
            {!isConfirmingSkip ? (
              <button
                type="button"
                className="onboarding-guide__skip-trigger"
                onClick={() => {
                  setSkipError(false);
                  setIsConfirmingSkip(true);
                }}
              >
                {t("onboarding.skip")}
              </button>
            ) : (
              <div className="onboarding-guide__skip-confirmation" role="group" aria-labelledby="onboarding-skip-title">
                <strong id="onboarding-skip-title">{t("onboarding.skipTitle")}</strong>
                <p>{t("onboarding.skipDescription")}</p>
                {skipError ? <small>{t("onboarding.skipFailed")}</small> : null}
                <div>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setIsConfirmingSkip(false)}
                    disabled={isSkipping}
                  >
                    {t("onboarding.skipCancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={confirmSkip}
                    disabled={isSkipping}
                  >
                    {t("onboarding.skipConfirm")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
